/**
 * AI ASSISTANCE API — the five catalogue-assistance routes.
 *
 * CONTRACT SOURCE: the API's M71 amendment A1 assistance contracts. The paths
 * below are relative to the transport's base URL (`/api/v1`), so `/ai/assistance/…`
 * here is `/api/v1/ai/assistance/…` on the wire.
 *
 * ---------------------------------------------------------------------------
 * THE TASK IS CHOSEN BY THE ROUTE
 * ---------------------------------------------------------------------------
 * There is no generic "generate JSON" endpoint and no `task` member in a body:
 * the path names the task, the path's request schema validates the body, and the
 * path's response schema validates the answer. A caller therefore cannot reach a
 * task its role does not cover by naming a different one.
 *
 * ---------------------------------------------------------------------------
 * A PROPOSAL IS NOT A WRITE
 * ---------------------------------------------------------------------------
 * Nothing here creates a record. Every method returns a PROPOSAL the merchant
 * reviews and saves through the ordinary catalogue API, which revalidates
 * everything. The methods spend the tenant's allowance, so the API treats them
 * as mutating, but no `Idempotency-Key` is required and none is sent.
 */
import { http } from '@/api/client/http';
import {
  AttributeOptionSuggestionResponseSchema,
  AttributeSuggestionResponseSchema,
  CategorySuggestionResponseSchema,
  ProductDraftSuggestionResponseSchema,
  VariantOptionSuggestionResponseSchema,
} from '@/api/schemas/ai-assistance';
import type {
  AttributeOptionProposal,
  AttributeOptionSuggestionRequest,
  AttributeProposal,
  AttributeSuggestionRequest,
  CategoryProposal,
  CategorySuggestionRequest,
  ProductDraftProposal,
  ProductDraftSuggestionRequest,
  VariantOptionProposal,
  VariantOptionSuggestionRequest,
} from '@/api/schemas/ai-assistance';

/** Tenant-scoped assistance. The tenant comes from the session, not the path. */
const ASSISTANCE_BASE = '/ai/assistance';

export const assistanceApi = {
  /**
   * POST /ai/assistance/category-suggestions — `catalog.category.suggest`.
   *
   * The existing categories are sent so the model REUSES what the tenant
   * already has instead of proposing a duplicate of it.
   */
  async suggestCategories(input: CategorySuggestionRequest): Promise<CategoryProposal> {
    const response = await http.post(
      `${ASSISTANCE_BASE}/category-suggestions`,
      CategorySuggestionResponseSchema,
      { body: input },
    );
    return response.data;
  },

  /**
   * POST /ai/assistance/attribute-suggestions — `catalog.attribute.suggest`.
   *
   * `categoryId` is the category the merchant picked, and `existingAttributes` is
   * the tenant's dictionary, so the model can reuse a definition rather than
   * propose a second one for the same thing.
   */
  async suggestAttributes(input: AttributeSuggestionRequest): Promise<AttributeProposal> {
    const response = await http.post(
      `${ASSISTANCE_BASE}/attribute-suggestions`,
      AttributeSuggestionResponseSchema,
      { body: input },
    );
    return response.data;
  },

  /**
   * POST /ai/assistance/attribute-option-suggestions —
   * `catalog.attribute-option.suggest`.
   */
  async suggestAttributeOptions(
    input: AttributeOptionSuggestionRequest,
  ): Promise<AttributeOptionProposal> {
    const response = await http.post(
      `${ASSISTANCE_BASE}/attribute-option-suggestions`,
      AttributeOptionSuggestionResponseSchema,
      { body: input },
    );
    return response.data;
  },

  /**
   * POST /ai/assistance/variant-option-suggestions —
   * `catalog.variant-option.suggest`.
   */
  async suggestVariantOptions(input: VariantOptionSuggestionRequest): Promise<VariantOptionProposal> {
    const response = await http.post(
      `${ASSISTANCE_BASE}/variant-option-suggestions`,
      VariantOptionSuggestionResponseSchema,
      { body: input },
    );
    return response.data;
  },

  /**
   * POST /ai/assistance/product-draft-suggestions —
   * `catalog.product-draft.suggest`.
   */
  async suggestProductDraft(input: ProductDraftSuggestionRequest): Promise<ProductDraftProposal> {
    const response = await http.post(
      `${ASSISTANCE_BASE}/product-draft-suggestions`,
      ProductDraftSuggestionResponseSchema,
      { body: input },
    );
    return response.data;
  },
};
